/* eslint-disable no-undef */
import { CURRENCY, SITE_URL, IS_PRODUCTION } from "utils/constants";
import guid from "utils/guid";

const eventId = () => `event_${guid()}`;
const getTotalPrice = (items) => Number(items.reduce((acc, item) => acc + item.quantity * item.price, 0).toFixed(2));

export const trackEvent = async (event, data) => {
	if (!IS_PRODUCTION)
		return console.log(`Track event: ${event}`, data);

	dataLayer.push({
		event,
		event_id: eventId(),
		...data,
	});
};

const trackEcomEvent = async (event, ecommerce) => {
	dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
	if (!IS_PRODUCTION)
		return console.log(`Track ecommerce event: ${event}`, ecommerce);

	trackEvent(event, {
		ecommerce: {
			...ecommerce,
			currency: CURRENCY,
		},
	});
};

const mapItems = (products) =>
	products.map((product, i) => {
		const {
			variant = {}, quantity = 1, title, shopifyTitle, shortDescription, discounts = [], type, index = i, isUpsell = false
		} = product || {};
		const item = {
			item_id: variant.sku,
			item_name: title || shopifyTitle,
			affiliation: "Every",
			currency: CURRENCY,
			index,
			description: shortDescription,
			item_brand: "Every",
			item_category: type,
			item_variant: variant.title,
			price: variant?.price,
			quantity,
			is_upsell: isUpsell,
		};
		if (discounts.length) {
			let itemDiscount = 0, coupons = [];
			product.discounts.forEach(discount => {
				itemDiscount += discount.amount;
				coupons.push(discount.title);
			});
			item.discount = itemDiscount;
			item.coupon = coupons.join(",");
		}
		return item;
	});

export const trackAddToCart = (products) => {
	const items = mapItems([].concat(products));
	trackEcomEvent("add_to_cart", {
		items,
		value: getTotalPrice(items),
	});
};

export const trackAddToSubscriptionCart = (products) => {
	const items = mapItems([].concat(products));
	trackEcomEvent("add_to_subscription_cart", {
		items,
		value: getTotalPrice(items),
	});
};

export const trackAddUpsellToSubscription = (product) => {
	const items = mapItems([].concat(product));
	trackEcomEvent("add_upsell_to_subscription", {
		items,
		value: getTotalPrice(items),
	});
};

export const trackRemoveFromCart = (products) => {
	const items = mapItems([].concat(products));
	trackEcomEvent("remove_from_cart", {
		items,
		value: getTotalPrice(items),
	});
};

export const trackViewItem = (product) => {
	trackEcomEvent("view_item", { items: mapItems([product]) });
	trackViewedProductKlaviyo(product);
};

export const trackViewItemList = ({ title, id, products }) =>
	trackEcomEvent("view_item_list", {
		item_list_name: title,
		item_list_id: id,
		items: mapItems(products),
	});

export const trackSelectItem = (product) => trackEcomEvent("select_item", { items: mapItems([product]) });

export const trackViewCart = (cart) => {
	trackEcomEvent("view_cart", {
		value: cart.totalPrice,
		items: mapItems(Object.values(cart.items)),
	});
};

export const trackBeginCheckout = (cart, is_one_time_purchase = false, discountCode) => {
	const ecommerce = {
		value: cart.totalPrice,
		items: mapItems(Object.values(cart.items)),
		is_one_time_purchase
	};
	const coupon = cart.discount?.code || discountCode;
	if (coupon) ecommerce.coupon = coupon;
	trackEcomEvent("begin_checkout", ecommerce);
};

export const trackSearchProducts = (search_text) => trackEvent("search_products", { search_text });

export const trackSearchBlogs = (search_text) => trackEvent("search_blogs", { search_text });

export const trackLogin = (customer) => trackEvent("login", { customer });

export const trackSignup = (customer) => trackEvent("sign_up", { customer });

export const trackSelectedBox = (box) => trackEvent("selected_box", { box });

export const trackSelectedMealPlan = (box, mealsPerWeek, deliveryPolicy) =>
	trackEvent("selected_meal_plan", {
		box, mealsPerWeek: mealsPerWeek || box.size.value / deliveryPolicy.intervalCount, deliveryPolicy
	});

export const trackEmailSignup = (email) => trackEvent("email_signup", { email });

export const trackPauseSubscription = (subscriptionId, reason, pauseDuration, flow = "pause") =>
	trackEvent("pause_subscription", { subscriptionId, reason, pauseDuration, flow });

export const trackRescheduleSubscription = (subscriptionId, reason, nextBillingDate, flow = "reschedule") =>
	trackEvent("reschedule_subscription", { subscriptionId, reason, nextBillingDate, flow });

const trackViewedProductKlaviyo = (product) => {
	const _learnq = window._learnq || [];
	const item = {
		ProductName: product.title,
		ProductID: product.id,
		Categories: product?.type,
		SKU: product?.variant?.sku,
		ImageURL: product?.image,
		URL: `${SITE_URL}/products/${product?.handle}`,
		Brand: "Every",
		Price: parseFloat(product?.variant?.price),
		CompareAtPrice: parseFloat(product?.variant?.compareAtPrice),
	};
	_learnq.push(["track", "Viewed Product", item]);
};

export const trackAppliedFilters = (appliedFilters) => trackEvent("applied_filters", { appliedFilters });

export const trackSaveChanges = (subscription) => {
	const {
		shopifyId: subscriptionId, nextBillingDate, totalWeight: total_quantity,
		totalLineItemDiscountedPrice: value, lines
	} = subscription;
	trackEvent("confirm_subscription_changes", {
		subscriptionId,
		nextBillingDate,
		ecommerce: {
			total_quantity,
			value,
			items: Object.values(lines).map(item => ({
				item_id: item.sku,
				item_name: item.name,
				affiliation: "Every",
				currency: CURRENCY,
				quantity: item.quantity,
				price: +item.price,
				discounted_price: +item.discountedPrice,
				item_variant: item.variantTitle
			})),
		}
	});
};
